/**
 * Profile Page Component
 * Protected page displaying user profile information and settings
 * Allows users to manage password and view account details
 *
 * @module pages/ProfilePage
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '@/utils/logger'
import { Layout, ProtectedRoute } from '@/components/layout'
import { Button } from '@/components/common'
import { ChangePassword } from '@/components/auth'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/utils/constants'

/**
 * Profile page component
 * Displays user information and settings
 * Protected route - requires authentication
 */
export default function ProfilePage() {
  const navigate = useNavigate()
  const auth = useAuth()
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [logoutLoading, setLogoutLoading] = useState(false)
  const [error, setError] = useState<string>('')

  logger.debug('[WALRUS] ProfilePage: rendering profile page', {
    userId: auth.user?.id,
    userEmail: auth.user?.email,
  })

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    logger.debug('[WALRUS] ProfilePage: initiating logout')
    setLogoutLoading(true)
    setError('')

    try {
      await auth.signOut()
      logger.info('[WALRUS] ProfilePage: logout successful')
      navigate(ROUTES.HOME, { replace: true })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Logout failed'
      logger.error('[WALRUS] ProfilePage: logout error', { error: errorMessage })
      setError(errorMessage)
      setLogoutLoading(false)
    }
  }

  /**
   * Format date for display
   */
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Unknown'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  return (
    <ProtectedRoute>
      <Layout contentClassName="bg-walrus-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
          {/* Header */}
          <div className="space-y-4">
            <h1 className="text-4xl sm:text-5xl font-bold text-walrus-black">
              Profile
            </h1>
            <p className="text-walrus-silver text-lg">
              Manage your account and settings
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg">
              <p className="font-semibold">Error</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Profile Information Card */}
          <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-8">
            <h2 className="text-2xl font-bold text-walrus-black mb-8">
              Account Information
            </h2>

            <div className="flex items-center gap-8 mb-8">
              {/* Avatar */}
              <div className="w-24 h-24 bg-walrus-gray rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-5xl">🦭</span>
              </div>

              {/* Basic Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-3xl font-bold text-walrus-black mb-2 truncate">
                  {auth.user?.displayName || auth.user?.email || 'User'}
                </h3>
                <p className="text-walrus-silver text-sm">
                  {auth.user?.role ? `Role: ${auth.user.role.charAt(0).toUpperCase() + auth.user.role.slice(1)}` : ''}
                </p>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid md:grid-cols-2 gap-8 border-t border-walrus-gray pt-8">
              {/* Email */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-walrus-silver uppercase tracking-wide">
                  Email Address
                </label>
                <p className="text-lg text-walrus-black break-all">
                  {auth.user?.email || 'Not available'}
                </p>
              </div>

              {/* Display Name */}
              {auth.user?.displayName && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-walrus-silver uppercase tracking-wide">
                    Display Name
                  </label>
                  <p className="text-lg text-walrus-black">
                    {auth.user.displayName}
                  </p>
                </div>
              )}

              {/* Member Since */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-walrus-silver uppercase tracking-wide">
                  Member Since
                </label>
                <p className="text-lg text-walrus-black">
                  {formatDate(auth.user?.createdAt)}
                </p>
              </div>

              {/* User ID */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-walrus-silver uppercase tracking-wide">
                  User ID
                </label>
                <p className="text-sm text-walrus-silver font-mono truncate" title={auth.user?.id}>
                  {auth.user?.id}
                </p>
              </div>
            </div>
          </div>

          {/* Security Settings */}
          <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-8">
            <h2 className="text-2xl font-bold text-walrus-black mb-8">
              Security & Password
            </h2>

            {!isChangingPassword ? (
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setIsChangingPassword(true)}
              >
                Change Password
              </Button>
            ) : (
              <div className="max-w-sm">
                <ChangePassword
                  onSuccess={() => {
                    logger.info('[WALRUS] ProfilePage: password changed successfully')
                    setIsChangingPassword(false)
                  }}
                  onCancel={() => {
                    logger.debug('[WALRUS] ProfilePage: password change cancelled')
                    setIsChangingPassword(false)
                  }}
                />
              </div>
            )}
          </div>

          {/* Additional Settings - Placeholder */}
          <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-8">
            <h2 className="text-2xl font-bold text-walrus-black mb-8">
              Preferences
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-walrus-white border border-walrus-gray rounded-lg">
                <div>
                  <h3 className="text-walrus-black font-semibold">Email Notifications</h3>
                  <p className="text-walrus-silver text-sm">
                    Receive updates about new photos in your favorite albums
                  </p>
                </div>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-5 h-5 rounded cursor-pointer"
                  aria-label="Email notifications"
                  disabled
                />
              </div>
            </div>
            <p className="text-walrus-silver text-sm mt-4">
              Additional preferences coming soon
            </p>
          </div>

          {/* Logout Button */}
          <div className="flex gap-4 pt-8">
            <Button
              variant="danger"
              size="lg"
              onClick={handleLogout}
              loading={logoutLoading}
            >
              Logout
            </Button>
            <p className="text-walrus-silver text-sm flex items-center">
              You can always sign back in with your email
            </p>
          </div>
        </div>

        {/* TODO: Add profile picture upload */}
        {/* TODO: Add display name editing */}
        {/* TODO: Add notification preferences UI */}
        {/* TODO: Add account deletion option */}
        {/* TODO: Add login history/sessions management */}
        {/* TODO: Add two-factor authentication setup */}
      </Layout>
    </ProtectedRoute>
  )
}
